import { SHIPPER_SCOPED_OVERRIDE_TYPES } from './modals/constants';

export const MARKETPLACE_KEY = 'ONWARD_MARKETPLACE';

// A contractor is excluded even when it also carries CARRIER — it belongs to its own tab.
export const isOrgFlexCenter = (client) => {
    const roles = client?.user?.roles || {};
    return !!roles['CARRIER'] && !roles['SHIPPER'] && !roles['CONTRACTOR'];
};

// Split carriers into assetHeavy and assetLight differentiators
export const buildCarrierAssetMaps = ({ integrationOverrides = [], pricingOverrides = [], adminClients = [] } = {}) => {
    const orgClientIds = new Set((adminClients || []).map((c) => c.client_id));
    const clientsMap = Object.fromEntries((adminClients || []).map((c) => [c.client_id, c]));

    // Role detection prefers roles embedded on the pricing payload (so external
    // carriers — those NOT in admin_clients — still resolve correctly), falling
    // back to clientsMap (admin_clients) for any side without an embedded record.
    const rolesFor = (client_id, embedded) => {
        const fromEmbedded = embedded?.user?.roles;
        if (fromEmbedded) return fromEmbedded;
        return clientsMap[client_id]?.user?.roles || {};
    };
    const isShipperOf = (client_id, embedded) => !!rolesFor(client_id, embedded)?.['SHIPPER'];
    const isCarrierOf = (client_id, embedded) => {
        const roles = rolesFor(client_id, embedded);
        return !!roles?.['CARRIER'] && !roles?.['SHIPPER'];
    };

    const assetHeavy = {};
    const assetLight = {};

    // Always surface the marketplace row in Network Carriers, even with no overrides.
    assetLight[MARKETPLACE_KEY] = { carrier: null };

    // Seed every org carrier so Flex Centers shows up even with no pricing/integration overrides.
    for (const client of adminClients || []) {
        if (isOrgFlexCenter(client)) {
            assetHeavy[client.client_id] ??= { carrier: client };
        }
    }

    const getMap = (carrier_id) => (orgClientIds.has(carrier_id) ? assetHeavy : assetLight);

    for (const override of integrationOverrides || []) {
        const { type, carrier_id, middle_mile_carrier_id, shipper_id, carrier, middle_mile_carrier } = override;
        // Shipper-scoped types (e.g. PC Miler mileage) have their own surface and are carrier-less;
        // don't bucket them as marketplace integrations or count them in the integrations card.
        if (SHIPPER_SCOPED_OVERRIDE_TYPES.includes(type)) continue;
        // Integrations not bound to a carrier belong to the marketplace (null) row.
        // The shipper still gets a section under MARKETPLACE_KEY so manual marketplace
        // connections surface; non-MANUAL types accumulate as configured integrations.
        const carrierId = carrier_id || middle_mile_carrier_id;
        const carrierObj = carrier || middle_mile_carrier;

        if (!carrierId) {
            assetLight[MARKETPLACE_KEY] ??= { carrier: null };
            if (shipper_id) {
                assetLight[MARKETPLACE_KEY][shipper_id] ??= {
                    integrations: [],
                    carrierPricing: [],
                    shipperPricing: [],
                };
                assetLight[MARKETPLACE_KEY][shipper_id].integrations.push(override);
            }
            continue;
        }
        const map = getMap(carrierId);
        map[carrierId] ??= { carrier: carrierObj };
        map[carrierId][shipper_id] ??= { integrations: [], carrierPricing: [], shipperPricing: [] };
        // MANUAL rows are surfaced as "needs setup" inside the integrations modal —
        // ShipperSection filters them out for the configured-count card.
        map[carrierId][shipper_id].integrations.push(override);
    }

    for (const p of pricingOverrides || []) {
        const clientIsShipper = isShipperOf(p.client_id, p.client);
        const clientIsCarrier = isCarrierOf(p.client_id, p.client);
        const partnerIsShipper = isShipperOf(p.partner_client_id, p.partner_client);
        const partnerIsCarrier = isCarrierOf(p.partner_client_id, p.partner_client);

        // Branch 1: carrier (client_id) → shipper (partner_client_id)
        if (clientIsCarrier && partnerIsShipper) {
            const carrier_id = p.client_id;
            const shipper_id = p.partner_client_id;
            const map = getMap(carrier_id);
            map[carrier_id] ??= { carrier: p.client };
            map[carrier_id][shipper_id] ??= { integrations: [], carrierPricing: [], shipperPricing: [] };
            map[carrier_id][shipper_id].carrierPricing.push(p);
        }
        // Branch 2: shipper (client_id) → carrier (partner_client_id)
        else if (clientIsShipper && partnerIsCarrier) {
            const shipper_id = p.client_id;
            const carrier_id = p.partner_client_id;
            if (!carrier_id) continue;
            const map = getMap(carrier_id);
            map[carrier_id] ??= { carrier: p.partner_client };
            map[carrier_id][shipper_id] ??= { integrations: [], carrierPricing: [], shipperPricing: [] };
            map[carrier_id][shipper_id].shipperPricing.push(p);
        }
        // Branch 3: shipper-side rate with no carrier counterpart on partner_client_id.
        // - assign_id set → "Rate Shop": pre-assigned to a specific carrier; drops into
        //   that carrier's shipperPricing.
        // - assign_id null → "Auto/Manual": marketplace; drops into the MARKETPLACE_KEY row.
        else if (clientIsShipper && !p.partner_client_id) {
            const shipper_id = p.client_id;
            if (p.assign_id) {
                const carrier_id = p.assign_id;
                const map = getMap(carrier_id);
                map[carrier_id] ??= { carrier: p.assign || null };
                map[carrier_id][shipper_id] ??= { integrations: [], carrierPricing: [], shipperPricing: [] };
                map[carrier_id][shipper_id].shipperPricing.push(p);
            } else {
                assetLight[MARKETPLACE_KEY] ??= { carrier: null };
                assetLight[MARKETPLACE_KEY][shipper_id] ??= {
                    integrations: [],
                    carrierPricing: [],
                    shipperPricing: [],
                };
                assetLight[MARKETPLACE_KEY][shipper_id].shipperPricing.push(p);
            }
        }
        // Fallback: when only one side resolves to a known role.
        // Common case: org shipper on one side, external carrier on the other (no admin_clients entry, no embedded user/roles).
        // Treat the unknown side as the carrier so external carriers still surface.
        else if (clientIsShipper && !partnerIsShipper && p.partner_client_id) {
            const shipper_id = p.client_id;
            const carrier_id = p.partner_client_id;
            const map = getMap(carrier_id);
            map[carrier_id] ??= { carrier: p.partner_client };
            map[carrier_id][shipper_id] ??= { integrations: [], carrierPricing: [], shipperPricing: [] };
            map[carrier_id][shipper_id].shipperPricing.push(p);
        } else if (partnerIsShipper && !clientIsShipper && p.client_id) {
            const carrier_id = p.client_id;
            const shipper_id = p.partner_client_id;
            const map = getMap(carrier_id);
            map[carrier_id] ??= { carrier: p.client };
            map[carrier_id][shipper_id] ??= { integrations: [], carrierPricing: [], shipperPricing: [] };
            map[carrier_id][shipper_id].carrierPricing.push(p);
        }
    }

    return { assetHeavy, assetLight };
};

export const buildContractorRows = (partnerships = []) => {
    const byContractor = new Map();

    for (const partnership of partnerships || []) {
        const contractor = partnership?.contractor;
        const contractorId = partnership?.contractor_id || contractor?.client_id;
        if (!contractorId) continue;

        if (!byContractor.has(contractorId)) {
            byContractor.set(contractorId, {
                client_id: contractorId,
                contractor: contractor || null,
                flexCenters: [],
            });
        }

        const row = byContractor.get(contractorId);
        const flexCenterId = partnership?.carrier_id;
        if (flexCenterId && !row.flexCenters.some((f) => f.client_id === flexCenterId)) {
            row.flexCenters.push({
                client_id: flexCenterId,
                business_name: partnership?.carrier?.business_name || null,
                partnership_id: partnership?.partnership_id || null,
                status: partnership?.status || null,
            });
        }
    }

    const byName = (a, b) => (a || '').localeCompare(b || '');

    return [...byContractor.values()]
        .map((row) => ({
            ...row,
            flexCenters: [...row.flexCenters].sort((a, b) => byName(a.business_name, b.business_name)),
        }))
        .sort((a, b) => byName(a.contractor?.business_name, b.contractor?.business_name));
};

export const visibleTabs = (tabs = [], { contractors = [] } = {}) =>
    (tabs || []).filter((tab) => (tab?.kind === 'contractor' ? (contractors || []).length > 0 : true));

export const filterContractorRows = (rows = [], search = '') => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return rows || [];

    return (rows || []).filter((row) =>
        ['business_name', 'email', 'client_id'].some((field) => {
            const value = row?.contractor?.[field] ?? (field === 'client_id' ? row?.client_id : null);
            return value && value.toString().toLowerCase().includes(q);
        })
    );
};
